import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet-async";

function Register() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    birthday: "",
    location: "",
    password: "",
    password_confirmation: "",
    role: "visiteur",
    accepted_terms: false,
    subscribe_newsletter: false,
  });

  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    setAvatar(e.target.files?.[0] || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.password_confirmation) {
      toast.error("Les mots de passe ne correspondent pas.", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    if (!formData.accepted_terms) {
      toast.error(
        "Vous devez accepter les CGU et la politique de confidentialité.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return;
    }

    try {
      setLoading(true);

      const payload = new FormData();

      payload.append("firstname", formData.firstname);
      payload.append("lastname", formData.lastname);
      payload.append("email", formData.email);
      payload.append("birthday", formData.birthday);
      payload.append("location", formData.location);
      payload.append("password", formData.password);
      payload.append("confirmPassword", formData.password_confirmation);
      payload.append("role", formData.role);

      payload.append(
        "termsAccepted",
        formData.accepted_terms ? "true" : "false"
      );

      payload.append(
        "newsletterSubscribed",
        formData.subscribe_newsletter ? "true" : "false"
      );

      if (avatar instanceof File) {
        payload.append("avatar", avatar);
      }

      await api.post("/auth/register", payload);

      toast.success("Inscription réussie ! Vérifiez votre email.", {
        position: "top-right",
        autoClose: 2500,
      });

      setTimeout(() => navigate("/login"), 1800);
    } catch (error) {
      console.error(error);

      const status = error.response?.status;
      const errors = error.response?.data?.errors;

      if (status === 400 || status === 422) {
        if (errors?.Email || errors?.email) {
          const message = errors.Email?.[0] || errors.email?.[0];

          toast.error(`Erreur email : ${message}`, {
            position: "top-right",
            autoClose: 5000,
          });
        } else if (errors?.Location || errors?.location) {
          const message = errors.Location?.[0] || errors.location?.[0];

          toast.error(`Erreur localité : ${message}`, {
            position: "top-right",
            autoClose: 5000,
          });
        } else if (errors?.Avatar || errors?.avatar) {
          const message = errors.Avatar?.[0] || errors.avatar?.[0];

          toast.error(`Erreur image : ${message}`, {
            position: "top-right",
            autoClose: 5000,
          });
        } else if (errors?.TermsAccepted || errors?.termsAccepted) {
          const message =
            errors.TermsAccepted?.[0] || errors.termsAccepted?.[0];

          toast.error(message, {
            position: "top-right",
            autoClose: 5000,
          });
        } else if (errors?.Password || errors?.password) {
          const message = errors.Password?.[0] || errors.password?.[0];

          toast.error(message, {
            position: "top-right",
            autoClose: 5000,
          });
        } else if (errors?.ConfirmPassword || errors?.confirmPassword) {
          const message =
            errors.ConfirmPassword?.[0] || errors.confirmPassword?.[0];

          toast.error(message, {
            position: "top-right",
            autoClose: 5000,
          });
        } else {
          toast.error("Erreur de validation. Vérifiez vos informations.", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } else {
        toast.error(
          error.response?.data?.message ||
            "Erreur lors de l'inscription. Veuillez réessayer.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Créer un compte | LMFP Photography</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100">
        <div className="mx-auto max-w-4xl">
          <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white shadow-sm">
            <div className="p-8 md:p-10">
              <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-blue-100">
                LMFP Photography
              </p>

              <h1 className="mb-3 text-3xl font-bold md:text-4xl">
                Créer un compte
              </h1>

              <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
                Rejoignez la plateforme pour découvrir les shootings, collaborer
                avec d’autres profils et accéder à votre espace personnel.
              </p>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
                <h2 className="mb-5 text-lg font-semibold text-white">
                  Informations personnelles
                </h2>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="firstname"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Prénom
                    </label>

                    <input
                      id="firstname"
                      name="firstname"
                      type="text"
                      placeholder="Votre prénom"
                      required
                      autoFocus
                      value={formData.firstname}
                      onChange={handleChange}
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="lastname"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Nom
                    </label>

                    <input
                      id="lastname"
                      name="lastname"
                      type="text"
                      placeholder="Votre nom"
                      required
                      value={formData.lastname}
                      onChange={handleChange}
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Email
                    </label>

                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="exemple@email.com"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="birthday"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Date de naissance
                    </label>

                    <input
                      type="date"
                      name="birthday"
                      id="birthday"
                      required
                      value={formData.birthday}
                      onChange={handleChange}
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="location"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Localité
                    </label>

                    <input
                      type="text"
                      name="location"
                      id="location"
                      placeholder="Ex : Bruxelles"
                      required
                      value={formData.location}
                      onChange={handleChange}
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label
                      htmlFor="avatar"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Photo de profil optionnelle
                    </label>

                    <input
                      type="file"
                      name="avatar"
                      id="avatar"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleFileChange}
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 file:mr-4 file:rounded-lg file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-950 hover:file:bg-slate-200"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label
                      htmlFor="role"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Rôle
                    </label>

                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none"
                    >
                      <option value="visiteur" className="text-black">
                        Visiteur
                      </option>

                      <option value="Modele" className="text-black">
                        Modèle
                      </option>

                      <option value="photographe" className="text-black">
                        Photographe
                      </option>

                      <option value="organisateur" className="text-black">
                        Organisateur d’événement
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
                <h2 className="mb-5 text-lg font-semibold text-white">
                  Sécurité du compte
                </h2>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Mot de passe
                    </label>

                    <input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="********"
                      required
                      minLength={8}
                      value={formData.password}
                      onChange={handleChange}
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="password_confirmation"
                      className="mb-2 block text-sm font-medium text-slate-200"
                    >
                      Confirmer le mot de passe
                    </label>

                    <input
                      id="password_confirmation"
                      name="password_confirmation"
                      type="password"
                      placeholder="********"
                      required
                      minLength={8}
                      value={formData.password_confirmation}
                      onChange={handleChange}
                      className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
                <h2 className="mb-5 text-lg font-semibold text-white">
                  Préférences
                </h2>

                <div className="space-y-4">
                  <label className="flex items-start gap-3 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      name="subscribe_newsletter"
                      checked={formData.subscribe_newsletter}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-white focus:ring-blue-500"
                    />

                    <span className="leading-6">
                      Je souhaite recevoir la newsletter et les nouveautés du
                      site.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      name="accepted_terms"
                      checked={formData.accepted_terms}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-white focus:ring-blue-500"
                    />

                    <span className="leading-6">
                      J’accepte les{" "}
                      <Link
                        to="/cgu"
                        className="text-white underline hover:text-slate-300"
                      >
                        Conditions Générales d’Utilisation
                      </Link>{" "}
                      et la{" "}
                      <Link
                        to="/confidentialite"
                        className="text-white underline hover:text-slate-300"
                      >
                        Politique de confidentialité
                      </Link>
                      .
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={loading || !formData.accepted_terms}
                  className="w-full rounded-xl bg-blue-600 px-8 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {loading ? "Inscription..." : "S'inscrire"}
                </button>

                <Link
                  to="/login"
                  className="text-center text-sm text-slate-400 transition hover:text-white sm:text-right"
                >
                  Déjà un compte ? Se connecter
                </Link>
              </div>
            </form>
          </section>

          <ToastContainer />
        </div>
      </div>
    </>
  );
}

export default Register;